import type { KnowledgeItemDto } from '@fos/shared/contracts/knowledge';
import { KNOWLEDGE_KIND_LABELS } from '@fos/shared/labels';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Download,
  ExternalLink,
  FileDown,
  FileQuestion,
  History,
  MoreHorizontal,
  Pencil,
  Plus,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { useRememberRecent } from '../../../lib/recent';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Markdown } from '../../../ui/Markdown';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../../ui/Menu';
import { type Crumb, PageHeader, SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON, TOUCH_ICON } from '../../mobile';
import { detailMeta, formatFileSize, hostOf } from '../knowledge-format';
import { AttachmentsSection } from './AttachmentsSection';
import { DecisionsList, SourceLink } from './DecisionsList';
import { canExportMarkdown, ExportMarkdownDialog, type MarkdownExportKind } from './ExportMarkdownDialog';
import { KnowledgeEditForm } from './KnowledgeEditForm';
import { NewNoteDialog } from './NewNoteDialog';
import { VersionsDialog } from './VersionsDialog';

function crumbs(item: KnowledgeItemDto, workspaceLabel: string): Crumb[] {
  const out: Crumb[] = [];
  if (item.client) out.push({ label: item.client.name, to: `/klienci/${item.client.id}` });
  if (item.project) out.push({ label: item.project.name, to: `/projekty/${item.project.id}` });
  if (out.length === 0) out.push({ label: workspaceLabel });
  return out;
}

function DetailSkeleton() {
  return (
    <div role="status" aria-label="Wczytywanie" className="flex flex-col gap-3">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="mt-6 h-40 w-full" />
    </div>
  );
}

/**
 * Szczegóły rekordu wiedzy (Figma 10 „Notatka z decyzjami”): okruszki klient › projekt, tytuł, „Edytuj”, linia meta
 * (wersja, data, kto widzi), źródło do otwarcia, treść Markdown, decyzje z tej notatki, załączniki; menu „…”:
 * historia wersji, eksport CLIENT.md / PROJECT.md, archiwum.
 */
export function KnowledgeDetail({
  id,
  workspaceLabel,
  backTo,
}: {
  id: string;
  workspaceLabel: string;
  /** mobile: link „Wiedza” nad nagłówkiem */
  backTo?: string;
}) {
  const access = useAccess();
  const utils = trpc.useUtils();
  const query = trpc.knowledge.get.useQuery({ id });
  const [editing, setEditing] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [exportTarget, setExportTarget] = useState<{ kind: MarkdownExportKind; id: string } | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  // inny rekord = bez trybu edycji
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset przy zmianie id
  useEffect(() => setEditing(false), [id]);
  const loaded = query.data;
  useRememberRecent(
    loaded
      ? {
          type: 'knowledge',
          id: loaded.id,
          title: loaded.title,
          subtitle: `${KNOWLEDGE_KIND_LABELS[loaded.kind].toLocaleLowerCase('pl-PL')} · ${loaded.project?.name ?? loaded.client?.name ?? 'firma'}`,
          to: `/wiedza/${loaded.id}`,
        }
      : null,
  );

  const setArchived = (archived: boolean) => (archived ? archive : restore);
  const onArchived = (item: KnowledgeItemDto) => {
    utils.knowledge.get.setData({ id }, item);
    void utils.knowledge.list.invalidate();
    void utils.knowledge.listDecisions.invalidate();
  };
  const archive = trpc.knowledge.archive.useMutation({
    onSuccess: (item) => {
      toast.success('Przeniesiono do archiwum', { description: item.title });
      onArchived(item);
    },
    onError: (err) => toast.error('Nie udało się zarchiwizować', { description: errorMessage(err) }),
  });
  const restore = trpc.knowledge.restore.useMutation({
    onSuccess: (item) => {
      toast.success('Przywrócono z archiwum', { description: item.title });
      onArchived(item);
    },
    onError: (err) => toast.error('Nie udało się przywrócić', { description: errorMessage(err) }),
  });

  const back = backTo ? (
    <Link
      to={backTo}
      className="mb-1 inline-flex min-h-11 items-center gap-1.5 rounded-xs text-body-s text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent md:hidden"
    >
      <ArrowLeft size={16} strokeWidth={1.75} aria-hidden />
      Wiedza
    </Link>
  ) : null;

  if (query.isPending) {
    return (
      <>
        {back}
        <DetailSkeleton />
      </>
    );
  }
  if (query.isError) {
    const code = appErrorCode(query.error);
    return (
      <>
        {back}
        <Section>
          {code === 'NOT_FOUND' ? (
            <EmptyState
              icon={FileQuestion}
              title="Nie ma takiej notatki"
              description="Mogła zostać usunięta albo nie masz już dostępu do jej klienta lub projektu."
              action={
                <Button asChild variant="secondary" className={TOUCH_BUTTON}>
                  <Link to="/wiedza">Wróć do Wiedzy</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
          )}
        </Section>
      </>
    );
  }

  const item = query.data;
  const exportKinds: { kind: MarkdownExportKind; id: string; label: string }[] = [];
  if (item.project && canExportMarkdown(access, 'project')) {
    exportKinds.push({ kind: 'project', id: item.project.id, label: 'Eksportuj PROJECT.md' });
  }
  if (item.client && canExportMarkdown(access, 'client')) {
    exportKinds.push({ kind: 'client', id: item.client.id, label: 'Eksportuj CLIENT.md' });
  }
  const canArchive = access.can('knowledge', 'edit') && (item.canEdit || item.archived);
  const hasBody =
    editing ||
    (item.kind === 'link' && Boolean(item.url)) ||
    (item.kind === 'file' && Boolean(item.file)) ||
    item.bodyMd.trim() !== '' ||
    item.kind === 'note';

  const actions = (
    <>
      {item.canEdit && !editing && (
        <Button variant="secondary" icon={Pencil} onClick={() => setEditing(true)} className={TOUCH_BUTTON}>
          Edytuj
        </Button>
      )}
      <Menu>
        <MenuTrigger asChild>
          <Button variant="ghost" icon={MoreHorizontal} aria-label="Więcej akcji" className={TOUCH_ICON} />
        </MenuTrigger>
        <MenuContent align="end">
          <MenuItem icon={History} onSelect={() => setVersionsOpen(true)}>
            Historia wersji
          </MenuItem>
          {exportKinds.map((e) => (
            <MenuItem
              key={e.kind}
              icon={FileDown}
              onSelect={() => {
                setExportTarget({ kind: e.kind, id: e.id });
                setExportOpen(true);
              }}
            >
              {e.label}
            </MenuItem>
          ))}
          {canArchive && (
            <>
              <MenuSeparator />
              <MenuItem
                icon={item.archived ? ArchiveRestore : Archive}
                onSelect={() => setArchived(!item.archived).mutate({ id: item.id, version: item.version })}
              >
                {item.archived ? 'Przywróć z archiwum' : 'Przenieś do archiwum'}
              </MenuItem>
            </>
          )}
        </MenuContent>
      </Menu>
    </>
  );

  return (
    <article className="flex flex-col gap-4 md:gap-6" aria-label={item.title}>
      <div>
        {back}
        <PageHeader
          detail
          breadcrumbs={crumbs(item, workspaceLabel)}
          title={item.title}
          meta={
            <span className="flex flex-col gap-1">
              <span>{detailMeta(item)}</span>
              {item.source && (
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="shrink-0 text-fg-muted">Źródło:</span>
                  <SourceLink source={item.source} className="text-body-s" />
                </span>
              )}
            </span>
          }
          actions={actions}
        />
      </div>

      {item.archived && (
        <ContextBanner tone="info" icon={Archive}>
          {KNOWLEDGE_KIND_LABELS[item.kind]} jest w archiwum. Przywróć, aby edytować.
        </ContextBanner>
      )}

      {/* treść notatki, decyzje i załączniki w białych sekcjach; nagłówek strony na tle */}
      {hasBody && (
        <Section>
          {editing ? (
            <KnowledgeEditForm
              item={item}
              onDone={() => setEditing(false)}
              onReload={() => {
                setEditing(false);
                void query.refetch();
              }}
            />
          ) : (
            <div className="flex flex-col gap-6">
              {item.kind === 'link' && item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 items-center gap-2 rounded-lg bg-surface px-4 py-3 text-body-s text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
                >
                  <ExternalLink size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
                  <span className="min-w-0 truncate">{hostOf(item.url)}</span>
                  <span className="min-w-0 truncate text-caption text-fg-muted">{item.url}</span>
                </a>
              )}
              {item.kind === 'file' && item.file && (
                <div className="flex items-center gap-3 rounded-lg bg-surface px-4 py-3">
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-body-s font-medium text-fg">{item.file.filename}</span>
                    <span className="text-caption text-fg-muted">
                      {item.file.typeLabel} · {formatFileSize(item.file.sizeBytes)}
                    </span>
                  </div>
                  <Button asChild variant="secondary" size="s" className={TOUCH_BUTTON}>
                    <a href={item.file.downloadUrl} download={item.file.filename}>
                      <Download size={14} strokeWidth={1.75} aria-hidden />
                      Pobierz
                    </a>
                  </Button>
                </div>
              )}
              {item.bodyMd.trim() ? (
                <Markdown>{item.bodyMd}</Markdown>
              ) : item.kind === 'note' ? (
                <p className="text-body-s text-fg-muted">Notatka nie ma jeszcze treści.</p>
              ) : null}
            </div>
          )}
        </Section>
      )}

      {item.kind === 'note' && (
        <Section aria-label="Decyzje z tej notatki">
          <SectionHeader
            title="Decyzje"
            action={
              item.canEdit ? (
                <Button
                  variant="ghost"
                  size="s"
                  className={TOUCH_BUTTON}
                  icon={Plus}
                  onClick={() => setDecisionOpen(true)}
                >
                  Dodaj decyzję
                </Button>
              ) : undefined
            }
          />
          <DecisionsList
            sourceItemId={item.id}
            emptyTitle="Brak decyzji z tej notatki"
            emptyDescription="Decyzja zapisana stąd będzie miała tę notatkę jako źródło."
          />
        </Section>
      )}

      <AttachmentsSection item={item} />

      <VersionsDialog item={item} open={versionsOpen} onOpenChange={setVersionsOpen} />
      {exportTarget && (
        <ExportMarkdownDialog
          kind={exportTarget.kind}
          id={exportTarget.id}
          open={exportOpen}
          onOpenChange={setExportOpen}
        />
      )}
      {item.canEdit && (
        <NewNoteDialog
          open={decisionOpen}
          onOpenChange={setDecisionOpen}
          defaultKind="decision"
          defaultClientId={item.client?.id ?? null}
          defaultProjectId={item.project?.id ?? null}
          sourceRef={{ type: 'knowledge_item', id: item.id }}
          onCreated={() => {
            void utils.knowledge.listDecisions.invalidate();
          }}
        />
      )}
    </article>
  );
}
